import { useState, useRef, useEffect, useCallback, Component, type ReactNode, type ErrorInfo } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api'
import { streamPost, readSseStream } from '@/lib/apiStream'
import { cn } from '@/lib/utils'
import {
  Bot, X, Send, Lightbulb, Mail, Search, Copy, Check, Trash2, Loader2, AlertTriangle,
} from 'lucide-react'

interface Message {
  role: 'user' | 'assistant'
  content: string
  timestamp: string
}

interface TicketAIAgentProps {
  ticketId: string
}

// ─── Error Boundary ────────────────────────────────────────────────────────

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

class AIAgentErrorBoundary extends Component<{ children: ReactNode; onReset: () => void }, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, error: null }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('TicketAIAgent crashed:', error, info.componentStack)
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="fixed bottom-20 right-6 z-50">
          <button
            onClick={() => {
              this.setState({ hasError: false, error: null })
              this.props.onReset()
            }}
            className="flex items-center gap-2 px-4 py-2 bg-red-50 border border-red-200 text-red-700 rounded-lg shadow-lg text-sm hover:bg-red-100 transition-colors"
          >
            <AlertTriangle className="w-4 h-4" />
            KI-Assistent Fehler – Klicken zum Neuladen
          </button>
        </div>
      )
    }
    return this.props.children
  }
}

// ─── Helpers ───────────────────────────────────────────────────────────────

// Simple markdown-like rendering (bold, code, lists)
function renderMarkdown(text: string): React.ReactNode[] {
  const lines = text.split('\n')
  const elements: React.ReactNode[] = []
  let inCodeBlock = false
  let codeContent = ''
  let codeKey = 0

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]!

    if (line.startsWith('```')) {
      if (inCodeBlock) {
        elements.push(
          <pre key={`code-${codeKey++}`} className="bg-gray-800 text-gray-100 rounded p-3 text-xs overflow-x-auto my-2 font-mono">
            {codeContent}
          </pre>
        )
        codeContent = ''
        inCodeBlock = false
      } else {
        inCodeBlock = true
      }
      continue
    }

    if (inCodeBlock) {
      codeContent += (codeContent ? '\n' : '') + line
      continue
    }

    if (line.trim() === '') {
      elements.push(<div key={`br-${i}`} className="h-2" />)
      continue
    }

    // Headers
    if (line.startsWith('### ')) {
      elements.push(<h4 key={i} className="font-semibold text-sm mt-3 mb-1">{line.slice(4)}</h4>)
      continue
    }
    if (line.startsWith('## ')) {
      elements.push(<h3 key={i} className="font-bold text-sm mt-3 mb-1">{line.slice(3)}</h3>)
      continue
    }
    if (line.startsWith('# ')) {
      elements.push(<h2 key={i} className="font-bold text-base mt-3 mb-1">{line.slice(2)}</h2>)
      continue
    }

    // List items
    if (line.match(/^[-*] /)) {
      elements.push(
        <div key={i} className="flex gap-1.5 ml-2">
          <span className="text-gray-400 shrink-0">•</span>
          <span>{renderInline(line.slice(2))}</span>
        </div>
      )
      continue
    }

    // Numbered list
    const numMatch = line.match(/^(\d+)\. /)
    if (numMatch) {
      elements.push(
        <div key={i} className="flex gap-1.5 ml-2">
          <span className="text-gray-400 shrink-0 font-mono text-xs">{numMatch[1]}.</span>
          <span>{renderInline(line.slice(numMatch[0].length))}</span>
        </div>
      )
      continue
    }

    elements.push(<p key={i}>{renderInline(line)}</p>)
  }

  return elements
}

function renderInline(text: string): React.ReactNode {
  // Bold, inline code, italic
  const parts: React.ReactNode[] = []
  let remaining = text
  let key = 0

  while (remaining.length > 0) {
    // Inline code
    const codeMatch = remaining.match(/^(.*?)`([^`]+)`(.*)$/)
    if (codeMatch) {
      if (codeMatch[1]) parts.push(<span key={key++}>{codeMatch[1]}</span>)
      parts.push(<code key={key++} className="bg-gray-100 text-pink-600 px-1 py-0.5 rounded text-xs font-mono">{codeMatch[2]}</code>)
      remaining = codeMatch[3] || ''
      continue
    }
    // Bold
    const boldMatch = remaining.match(/^(.*?)\*\*([^*]+)\*\*(.*)$/)
    if (boldMatch) {
      if (boldMatch[1]) parts.push(<span key={key++}>{boldMatch[1]}</span>)
      parts.push(<strong key={key++}>{boldMatch[2]}</strong>)
      remaining = boldMatch[3] || ''
      continue
    }
    parts.push(<span key={key++}>{remaining}</span>)
    break
  }

  return <>{parts}</>
}

export function TicketAIAgent({ ticketId }: TicketAIAgentProps) {
  const [resetKey, setResetKey] = useState(0)
  return (
    <AIAgentErrorBoundary onReset={() => setResetKey((k) => k + 1)}>
      <TicketAIAgentInner key={resetKey} ticketId={ticketId} />
    </AIAgentErrorBoundary>
  )
}

function TicketAIAgentInner({ ticketId }: TicketAIAgentProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [input, setInput] = useState('')
  const [isStreaming, setIsStreaming] = useState(false)
  const [localMessages, setLocalMessages] = useState<Message[]>([])
  const [streamingContent, setStreamingContent] = useState('')
  const [copiedIdx, setCopiedIdx] = useState<number | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const queryClient = useQueryClient()

  // Load conversation from DB
  const { data: conversationData } = useQuery({
    queryKey: ['ai-conversation', ticketId],
    queryFn: async () => {
      const { data } = await apiClient.get(`/ai/ticket-agent/${ticketId}/conversation`)
      const raw = data as { messages?: unknown; conversationId?: string | null }
      return {
        messages: Array.isArray(raw?.messages) ? raw.messages as Message[] : [],
        conversationId: raw?.conversationId ?? null,
      }
    },
    enabled: isOpen,
    retry: 1,
  })

  // Sync DB messages to local state (only when query data actually changes,
  // NOT when isStreaming toggles — that would overwrite with stale data)
  useEffect(() => {
    if (conversationData?.messages && Array.isArray(conversationData.messages) && !isStreaming) {
      setLocalMessages(conversationData.messages)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationData?.messages])

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [localMessages, streamingContent])

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + 'px'
    }
  }, [input])

  const sendMessage = useCallback(async (
    messageText: string,
    actions?: ('draft_email' | 'suggest_solution' | 'find_similar')[],
    opts?: { insertAsEmailDraft?: boolean },
  ) => {
    if (!messageText.trim() || isStreaming) return

    const userMessage: Message = {
      role: 'user',
      content: messageText.trim(),
      timestamp: new Date().toISOString(),
    }

    setLocalMessages(prev => [...prev, userMessage])
    setInput('')
    setIsStreaming(true)
    setStreamingContent('')

    try {
      const response = await streamPost(`/ai/ticket-agent/${ticketId}/message`, {
        message: messageText.trim(),
        actions,
      })

      if (!response.ok) {
        const text = await response.text().catch(() => '')
        let errorMsg = `HTTP ${response.status}`
        try {
          const parsed = JSON.parse(text) as { error?: string; message?: string }
          errorMsg = parsed.error || parsed.message || errorMsg
        } catch {
          if (text) errorMsg = text.substring(0, 200)
        }
        throw new Error(errorMsg)
      }

      let fullContent = ''

      for await (const event of readSseStream(response)) {
        if (event.type === 'content' && typeof event.text === 'string') {
          fullContent += event.text
          setStreamingContent(fullContent)
        } else if (event.type === 'error') {
          throw new Error((event.message as string | undefined) || 'Streaming-Fehler')
        }
      }

      // Add assistant message to local state
      const assistantMessage: Message = {
        role: 'assistant',
        content: fullContent,
        timestamp: new Date().toISOString(),
      }
      setLocalMessages(prev => [...prev, assistantMessage])
      setStreamingContent('')

      // If this was an email draft request, dispatch event to open reply form
      if (opts?.insertAsEmailDraft && fullContent) {
        window.dispatchEvent(new CustomEvent('ai-email-draft', { detail: fullContent }))
      }

      // Invalidate query to sync with DB
      queryClient.invalidateQueries({ queryKey: ['ai-conversation', ticketId] })
    } catch (err) {
      const errorMessage: Message = {
        role: 'assistant',
        content: `Fehler: ${(err as Error).message}`,
        timestamp: new Date().toISOString(),
      }
      setLocalMessages(prev => [...prev, errorMessage])
      setStreamingContent('')
    } finally {
      setIsStreaming(false)
    }
  }, [ticketId, isStreaming, queryClient])

  const handleClearConversation = async () => {
    try {
      await apiClient.delete(`/ai/ticket-agent/${ticketId}/conversation`)
      setLocalMessages([])
      queryClient.invalidateQueries({ queryKey: ['ai-conversation', ticketId] })
    } catch {
      // ignore
    }
  }

  const handleCopy = (text: string, idx: number) => {
    navigator.clipboard.writeText(text)
    setCopiedIdx(idx)
    setTimeout(() => setCopiedIdx(null), 2000)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage(input)
    }
  }

  // Floating button (always visible)
  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-20 right-6 z-50 w-14 h-14 bg-brand-600 hover:bg-brand-700 text-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-105"
        title="KI-Assistent"
      >
        <Bot className="w-6 h-6" />
        {localMessages.length > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
            {localMessages.filter(m => m.role === 'assistant').length}
          </span>
        )}
      </button>
    )
  }

  return (
    <div className="fixed bottom-6 right-6 z-50 w-[420px] h-[600px] bg-white rounded-xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-brand-600 text-white shrink-0">
        <div className="flex items-center gap-2">
          <Bot className="w-5 h-5" />
          <span className="font-semibold text-sm">KI-Assistent</span>
          <span className="text-xs opacity-75">#{ticketId}</span>
        </div>
        <div className="flex items-center gap-1">
          {localMessages.length > 0 && (
            <button
              onClick={handleClearConversation}
              className="p-1.5 hover:bg-white/20 rounded transition-colors"
              title="Konversation loeschen"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={() => setIsOpen(false)}
            className="p-1.5 hover:bg-white/20 rounded transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {localMessages.length === 0 && !isStreaming && (
          <div className="text-center text-gray-400 mt-8">
            <Bot className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="text-sm font-medium">KI-Assistent bereit</p>
            <p className="text-xs mt-1">Stelle eine Frage oder nutze die Schnellaktionen</p>
          </div>
        )}

        {localMessages.map((msg, i) => (
          <div key={i} className={cn('flex', msg.role === 'user' ? 'justify-end' : 'justify-start')}>
            <div
              className={cn(
                'max-w-[85%] rounded-lg px-3 py-2 text-sm',
                msg.role === 'user'
                  ? 'bg-brand-600 text-white'
                  : 'bg-gray-100 text-gray-800',
              )}
            >
              {msg.role === 'assistant' ? (
                <div className="space-y-0.5">
                  {renderMarkdown(msg.content)}
                  <button
                    onClick={() => handleCopy(msg.content, i)}
                    className="mt-2 flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {copiedIdx === i ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                    {copiedIdx === i ? 'Kopiert' : 'Kopieren'}
                  </button>
                </div>
              ) : (
                <div className="whitespace-pre-wrap">{msg.content}</div>
              )}
            </div>
          </div>
        ))}

        {/* Streaming indicator */}
        {isStreaming && (
          <div className="flex justify-start">
            <div className="max-w-[85%] rounded-lg px-3 py-2 text-sm bg-gray-100 text-gray-800">
              {streamingContent ? (
                <div className="space-y-0.5">{renderMarkdown(streamingContent)}</div>
              ) : (
                <div className="flex items-center gap-2 text-gray-400">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-xs">Denke nach...</span>
                </div>
              )}
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Quick Actions */}
      <div className="px-3 py-2 border-t border-gray-100 flex gap-1.5 shrink-0">
        <button
          disabled={isStreaming}
          onClick={() => sendMessage('Analysiere dieses Ticket und schlage konkrete Loesungsschritte vor.', ['suggest_solution'])}
          className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-lg transition-colors disabled:opacity-50"
        >
          <Lightbulb className="w-3 h-3" />
          Loesung
        </button>
        <button
          disabled={isStreaming}
          onClick={() => sendMessage('Entwirf eine professionelle Antwort-E-Mail an den Kunden zu diesem Ticket.', ['draft_email'], { insertAsEmailDraft: true })}
          className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors disabled:opacity-50"
        >
          <Mail className="w-3 h-3" />
          E-Mail
        </button>
        <button
          disabled={isStreaming}
          onClick={() => sendMessage('Suche nach aehnlichen Faellen dieses Kunden und zeige relevante Loesungen.', ['find_similar'])}
          className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 rounded-lg transition-colors disabled:opacity-50"
        >
          <Search className="w-3 h-3" />
          Aehnliche Faelle
        </button>
      </div>

      {/* Input */}
      <div className="px-3 pb-3 shrink-0">
        <div className="flex items-end gap-2 bg-gray-50 rounded-lg border border-gray-200 p-2">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Frage zum Ticket stellen..."
            disabled={isStreaming}
            className="flex-1 bg-transparent text-sm resize-none outline-none placeholder-gray-400 max-h-[120px]"
            rows={1}
          />
          <button
            onClick={() => sendMessage(input)}
            disabled={!input.trim() || isStreaming}
            className="p-2 bg-brand-600 hover:bg-brand-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  )
}
